import React, { useState, useEffect } from 'react';
import PageHeader from '../../../components/PageHeader';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { adminUserService } from '../services/adminUserService';
import { useAuth } from '../../auth';

interface UserItem {
  _id: string;
  email: string;
  name: string;
  picture: string;
  status: 'pending' | 'approved' | 'blocked';
  role: 'user' | 'admin';
  createdAt: string;
  approvedAt?: string;
}

function getStatusLabel(status: string): string {
  if (status === 'approved') return 'Aprovado';
  if (status === 'pending') return 'Pendente';
  return 'Bloqueado';
}

function getStatusClass(status: string): string {
  if (status === 'approved') return 'bg-green-100 text-green-700';
  if (status === 'pending') return 'bg-yellow-100 text-yellow-700';
  return 'bg-red-100 text-red-700';
}

function getFilterLabel(f: string, pendingCount: number): string {
  if (f === 'all') return 'Todos';
  if (f === 'pending') return `Pendentes (${pendingCount})`;
  if (f === 'approved') return 'Aprovados';
  return 'Bloqueados';
}

const AdminUsersPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<UserItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'blocked'>('all');

  const load = async () => {
    const res = await adminUserService.list();
    setUsers(res.data.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  if (currentUser?.role !== 'admin') {
    return <div className="p-8 text-center text-red-600">Acesso restrito a administradores.</div>;
  }

  if (loading) return <LoadingSpinner />;

  const filtered = filter === 'all' ? users : users.filter(u => u.status === filter);
  const pendingCount = users.filter(u => u.status === 'pending').length;

  const handleApprove = async (id: string) => {
    await adminUserService.approve(id);
    await load();
  };

  const handleBlock = async (id: string) => {
    if (!window.confirm('Bloquear este usuário?')) return;
    await adminUserService.block(id);
    await load();
  };

  const handleToggleRole = async (id: string, currentRole: string) => {
    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    if (!window.confirm(`Alterar para ${newRole}?`)) return;
    await adminUserService.setRole(id, newRole);
    await load();
  };

  return (
    <>
      <PageHeader title="Administração de Usuários" />
      <div className="p-4 sm:p-6 space-y-4">
        {/* Filtros */}
        <div className="flex gap-2 flex-wrap">
          {(['all', 'pending', 'approved', 'blocked'] as const).map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1 text-xs rounded-full border ${filter === f ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300'}`}>
              {getFilterLabel(f, pendingCount)}
            </button>
          ))}
        </div>

        {/* Lista */}
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="divide-y">
            {filtered.map(u => (
              <div key={u._id} className="p-4 sm:p-5">
                {/* Mobile */}
                <div className="sm:hidden space-y-3">
                  <div className="flex items-center gap-3">
                    <img src={u.picture} alt={u.name} className="w-10 h-10 rounded-full shrink-0" referrerPolicy="no-referrer" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{u.name}</p>
                      <p className="text-xs text-gray-500 truncate">{u.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${getStatusClass(u.status)}`}>
                      {getStatusLabel(u.status)}
                    </span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>
                      {u.role === 'admin' ? 'Admin' : 'Usuário'}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    {u.status === 'pending' && (
                      <button onClick={() => handleApprove(u._id)} className="flex-1 py-2 text-xs font-medium bg-green-600 text-white rounded-lg hover:bg-green-700">Aprovar</button>
                    )}
                    {u.status !== 'blocked' && u._id !== currentUser?.id && (
                      <button onClick={() => handleBlock(u._id)} className="flex-1 py-2 text-xs font-medium bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100">Bloquear</button>
                    )}
                    {u.status === 'approved' && u._id !== currentUser?.id && (
                      <button onClick={() => handleToggleRole(u._id, u.role)} className="flex-1 py-2 text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200 rounded-lg hover:bg-purple-100">
                        {u.role === 'admin' ? '→ Usuário' : '→ Admin'}
                      </button>
                    )}
                  </div>
                </div>

                {/* Desktop */}
                <div className="hidden sm:flex sm:items-center sm:justify-between">
                  <div className="flex items-center gap-4">
                    <img src={u.picture} alt={u.name} className="w-12 h-12 rounded-full border-2 border-gray-200" referrerPolicy="no-referrer" />
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-medium text-gray-900">{u.name}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStatusClass(u.status)}`}>
                          {getStatusLabel(u.status)}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>
                          {u.role === 'admin' ? 'Admin' : 'Usuário'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">{u.email}</p>
                      {u.approvedAt && <p className="text-xs text-gray-400 mt-0.5">Aprovado em {new Date(u.approvedAt).toLocaleDateString('pt-BR')}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {u.status === 'pending' && (
                      <button onClick={() => handleApprove(u._id)} className="px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700">Aprovar</button>
                    )}
                    {u.status !== 'blocked' && u._id !== currentUser?.id && (
                      <button onClick={() => handleBlock(u._id)} className="px-4 py-2 text-sm font-medium text-red-700 border border-red-300 rounded-lg hover:bg-red-50">Bloquear</button>
                    )}
                    {u.status === 'approved' && u._id !== currentUser?.id && (
                      <button onClick={() => handleToggleRole(u._id, u.role)} className="px-4 py-2 text-sm font-medium text-purple-700 border border-purple-300 rounded-lg hover:bg-purple-50">
                        {u.role === 'admin' ? 'Remover Admin' : 'Tornar Admin'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminUsersPage;
